import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {
  CONFIGURATION_SETTING_KEYS,
  IBuyResponse,
  IDirectTicket,
  IGuestTransactionRequest,
  IPurchase,
  ITransactionRequest,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom, Observable } from 'rxjs';
import { ConfigurationStore } from '../../configuration/configuration-store.service';
import {
  ActiveRelationTicketGateway,
  IRelationTicketRequest,
} from './active-relation-ticket.gateway';
import { ActiveFixedPriceTicketsGateway } from './active-fixed-price-tickets.gateway';
import { EConfigurationChannel } from '@web/dtos';

type RelationTicketMask = Pick<
  IRelationTicketRequest,
  'origin' | 'destination' | 'via' | 'dateTime'
>;

interface IDirectTicketRequest {
  serviceAreaId: number;
  isRelationSearchAvailable: boolean;
  relationRequest: RelationTicketMask | undefined;
}

interface IFixedPriceTicketRequest {
  serviceAreaId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ActiveDirectTicketStore {
  private readonly fixedPriceTicketGateway: ActiveFixedPriceTicketsGateway =
    new ActiveFixedPriceTicketsGateway();
  private readonly relationTicketGateway: ActiveRelationTicketGateway =
    new ActiveRelationTicketGateway();
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );
  private readonly isRelationSearchActive = computed(() =>
    this.configurationStore.getBooleanConfiguration(
      CONFIGURATION_SETTING_KEYS.IsRelationSearchActive
    )
  );

  private readonly ticketName: WritableSignal<string> = signal('');
  private readonly relationRequest: WritableSignal<
    RelationTicketMask | undefined
  > = signal(undefined);
  private readonly relationTicketRequest: Signal<IDirectTicketRequest> =
    computed(() => {
      return {
        serviceAreaId: this.serviceAreaId(),
        isRelationSearchAvailable: this.isRelationSearchActive(),
        relationRequest: this.relationRequest(),
      };
    });
  private readonly fixedPriceTicketRequest: Signal<IFixedPriceTicketRequest> =
    computed((): IFixedPriceTicketRequest => {
      return {
        serviceAreaId: this.serviceAreaId(),
      };
    });

  #fixedPriceTicketResource: ResourceRef<
    IApiResponse<IDirectTicket[]> | undefined
  > = resource({
    params: this.fixedPriceTicketRequest,
    loader: ({
      params,
    }: ResourceLoaderParams<IFixedPriceTicketRequest>): Promise<
      IApiResponse<IDirectTicket[]> | undefined
    > => {
      return firstValueFrom(
        this.fixedPriceTicketGateway.list(params.serviceAreaId)
      );
    },
  });

  #relationTicketResource: ResourceRef<
    IApiResponse<IDirectTicket[]> | undefined
  > = resource({
    params: this.relationTicketRequest,
    loader: ({
      params,
    }: ResourceLoaderParams<IDirectTicketRequest>): Promise<
      IApiResponse<IDirectTicket[]> | undefined
    > => {
      if (
        params == undefined ||
        (!params.relationRequest && params.isRelationSearchAvailable)
      ) {
        return Promise.resolve(undefined);
      }
      if (params.relationRequest && params.isRelationSearchAvailable) {
        return firstValueFrom(
          this.relationTicketGateway.list({
            serviceAreaId: params.serviceAreaId,
            origin: params.relationRequest.origin,
            destination: params.relationRequest.destination,
            dateTime: params.relationRequest.dateTime,
            via: params.relationRequest.via,
          })
        );
      }
      return firstValueFrom(
        this.fixedPriceTicketGateway.list(params.serviceAreaId)
      );
    },
  });

  readonly isLoadingFixedPriceTickets: Signal<boolean> = computed((): boolean =>
    this.#fixedPriceTicketResource.isLoading()
  );

  readonly isLoadingRelationTickets: Signal<boolean> = computed((): boolean =>
    this.#relationTicketResource.isLoading()
  );

  readonly isLoading: Signal<boolean> = computed(
    (): boolean =>
      this.isLoadingRelationTickets() || this.isLoadingFixedPriceTickets()
  );

  readonly tickets: Signal<IDirectTicket[]> = computed(() => {
    return this.fixedPriceTickets().concat(this.relationTickets());
  });

  readonly fixedPriceTickets: Signal<IDirectTicket[]> = computed(
    (): IDirectTicket[] =>
      this.#fixedPriceTicketResource.hasValue()
        ? (this.#fixedPriceTicketResource.value()?.data ?? [])
        : []
  );

  public writableFixedPriceTickets: WritableSignal<IDirectTicket[]> = signal<
    IDirectTicket[]
  >([]);

  public writableRelationTickets: WritableSignal<IDirectTicket[]> = signal<
    IDirectTicket[]
  >([]);

  readonly relationTickets: Signal<IDirectTicket[]> = computed(
    (): IDirectTicket[] =>
      this.#relationTicketResource.hasValue()
        ? (this.#relationTicketResource.value()?.data ?? [])
        : []
  );

  readonly ticketConfiguration: Signal<IDirectTicket | undefined> = computed(
    (): IDirectTicket | undefined => {
      return this.tickets().find(
        (ticket: IDirectTicket) => ticket.name === this.ticketName()
      );
    }
  );

  public fixedPriceChanges: Signal<boolean> = computed(
    (): boolean => this.writableFixedPriceTickets() !== this.fixedPriceTickets()
  );

  public loadTicketConfiguration(ticketName: string): void {
    this.ticketName.set(ticketName);
  }

  public loadRelationTicket(request: RelationTicketMask): void {
    this.relationRequest.set(request);
  }

  public placeOrder(
    request: ITransactionRequest
  ): Observable<IApiResponse<IPurchase>> {
    return this.fixedPriceTicketGateway.placeOrder(request);
  }

  public placeGuestOrder(
    request: IGuestTransactionRequest
  ): Observable<IApiResponse<IBuyResponse>> {
    return this.fixedPriceTicketGateway.placeGuestOrder(request);
  }

  public reloadFixedPriceTickets(): void {
    this.#fixedPriceTicketResource.reload();
  }
}
